import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Divider } from '@mui/material'

import scanApi from '../api/scan'
import useHandleError from '../api/useHandleError'
import ScanFilters from '../components/filters/ScanFilters'
import ScanActionsInput from '../components/inputs/ScanActionsInput'
import ScanTable from '../components/tables/ScanTable'
import { setViewportWidth } from '../useViewPort'

export const ScanRowsContext = createContext()

const ScanPage = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [isResultEmpty, setIsResultEmpty] = useState(false)
  const [filters, setFilters] = useState({})
  const [selectedRows, setSelectedRows] = useState(new Set())
  const [currentPage, setCurrentPage] = useState(2)
  const [maxRowsCount, setMaxRowsCount] = useState(null)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [isRefreshed, setIsRefreshed] = useState(false)

  const handleError = useHandleError()
  const actualPageDataRef = useRef(null)
  const previousFilters = useRef({})

  const sanitizeFilters = useCallback((activeFilters) => {
    const cleaned = {}

    Object.entries(activeFilters).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        cleaned[key] = value
        return
      }

      if (value !== undefined && value !== null && value !== '') {
        cleaned[key] = value
      }
    })

    return cleaned
  }, [])

  const getRows = useCallback(
    async (usedFilters) => {
      setIsLoading(true)
      const params = sanitizeFilters(usedFilters)
      let actualPage = null

      if (currentPage === 2) {
        actualPage = 0
        actualPageDataRef.current = null
      } else {
        actualPage = currentPage - 1
      }

      let pages = []
      while (actualPage < currentPage) {
        actualPage++

        if (actualPage !== 1 && actualPageDataRef.current?.next) {
          const url = new URL(actualPageDataRef.current.next)
          params.cursor = url.searchParams.get('cursor')
        }

        actualPageDataRef.current = await scanApi
          .getScans(params)
          .then((data) => {
            if (actualPage === 1 && data.status === 204) {
              setIsResultEmpty(true)
              setHasNextPage(false)
              return {
                count: 0,
                next: null,
                previous: null,
                results: [],
              }
            }

            setIsResultEmpty(false)
            return data.data
          })
          .catch(handleError)

        if (!actualPageDataRef.current) {
          return null
        }

        if (actualPageDataRef.current.count >= 0) {
          setMaxRowsCount(actualPageDataRef.current.count)
        }

        pages = [...pages, ...actualPageDataRef.current.results]

        if (actualPageDataRef.current.next === null) {
          break
        }
      }

      setHasNextPage(Boolean(actualPageDataRef.current?.next))
      return { ...actualPageDataRef.current, results: pages }
    },
    [currentPage, handleError, sanitizeFilters]
  )

  useEffect(() => {
    const fetchData = async () => {
      if (currentPage) {
        const res = await getRows(filters)
        if (res) {
          setRows((prevRows) => (currentPage <= 2 ? res.results : [...prevRows, ...res.results]))
        }
      }
      setIsLoading(false)
    }

    fetchData()
  }, [currentPage, isRefreshed])

  const getNewData = () => {
    setCurrentPage(2)
    setIsRefreshed((prev) => !prev)
    setIsLoading(false)
    setRows([])
    setSelectedRows(new Set())
    setMaxRowsCount(null)
    setHasNextPage(false)
  }

  const clearFilters = () => {
    setFilters({})
  }

  useEffect(() => {
    const hasFiltersChanged = JSON.stringify(previousFilters.current) !== JSON.stringify(filters)
    if (hasFiltersChanged) {
      getNewData()
    }

    previousFilters.current = filters
  }, [filters])

  useEffect(() => {
    setTimeout(() => setViewportWidth())
  }, [rows.length, isLoading])

  const contextValue = useMemo(
    () => ({
      selectedRows,
      setSelectedRows,
      rows,
      isResultEmpty,
      isLoading,
      setIsLoading,
      currentPage,
      setCurrentPage,
      maxRowsCount,
      hasNextPage,
    }),
    [selectedRows, rows, isResultEmpty, isLoading, currentPage, maxRowsCount, hasNextPage]
  )

  return (
    <ScanRowsContext.Provider value={contextValue}>
      <ScanFilters setFilters={setFilters} usedFilters={filters} />
      <Divider />
      <ScanActionsInput
        getNewData={getNewData}
        clearFilters={clearFilters}
        isLoading={isLoading}
        filters={filters}
        selectedRows={selectedRows}
        maxRowsCount={maxRowsCount}
      />
      <ScanTable />
    </ScanRowsContext.Provider>
  )
}

export default ScanPage
